import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import Loader from 'components/shared/loader/loader.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { DirectoryBrowserConfig, DirectoryLab, DirectoryPage } from './directory-browser-config';
import SupplierMap from './supplier-map.component';

/** The browser's current filters, as directory query variables. */
export type SupplierDirectoryFilterVariables = Record<string, unknown>;

type SupplierMapDialogProps = {
    open: boolean;
    onClose: () => void;
    filterVariables: SupplierDirectoryFilterVariables;
    onSelect: (lab: DirectoryLab) => void;
    config: DirectoryBrowserConfig;
};

/**
 * Every located lab or clinic at once; the server caps it per directory (500 suppliers or
 * labs, 5000 clinics — DD-3698), and the map clusters the pins.
 */
const MAP_PAGE_SIZE = 5000;

/**
 * DD-3674: the browser's suppliers as pins in a large dialog (labs with a map position
 * only), with the list's filters. A pin opens the same profile as "Read more".
 */
const SupplierMapDialog: FC<SupplierMapDialogProps> = ({ open, onClose, filterVariables, onSelect, config }) => {
    const { t: translate } = useTranslation(['client-directory']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { data } = useQuery<Record<string, DirectoryPage>>(config.query, {
        variables: { ...filterVariables, onlyLocated: true, limit: MAP_PAGE_SIZE, offset: 0 },
        skip: !open,
        fetchPolicy: 'cache-and-network',
        onError: (error) => showBoundary(error),
    });
    const page = data?.[config.field];

    return (
        <DHDialog open={open} onClose={onClose} fullWidth maxWidth="xl" title={translate(config.texts.mapTitle)}>
            {page ? (
                <SupplierMap labs={page.labs} onSelect={onSelect} onlyLocatedKey={config.texts.mapOnlyLocated} />
            ) : (
                <Loader />
            )}
        </DHDialog>
    );
};

export default SupplierMapDialog;
